import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";
import { useAutoSync } from "./hooks/useAutoSync";
import { initAutoSync } from "./utils/autoSync";
import { apiFetch, getAccessToken, setTokens, faceLogin, isPersonalDevice } from "./services/api";
import { getStoredFaceSecret, saveRosterFaces, syncPendingFaceRegistration } from "./hooks/useFaceAuth";
import { getOfflineSecret, offlineAuthReady, getBundleByUserId } from "./utils/offlineAuth";
import { saveFeatures, hasFeature } from "./utils/featureFlags";
import { setBeforeOnline } from './utils/netGate';
import { pushSupported, enablePush } from "./services/push";
import { saveAlcoholThresholds, type AlcoholThresholds } from "./utils/alcoholThresholds";
import { isSimpleMode } from "./utils/simpleMode";
import { isAdminMonitor } from "./utils/adminMonitor";
import { SimpleHomePage } from "./pages/SimpleHomePage";
import { fetchOfflineRoster, verifyBoarding } from "./services/api";
import { LoginPage } from "./pages/LoginPage";
import { HomePage } from "./pages/HomePage";
import PersonalStartPage from "./pages/PersonalStartPage";
import { TimestampPage } from "./pages/TimestampPage";
import HistoryPage from "./pages/HistoryPage";
import { HistoryDetailPage } from "./pages/HistoryDetailPage";
import { MessagesPage } from "./pages/MessagesPage";
import { AlertsPage } from "./pages/AlertsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { SyncPage } from "./pages/SyncPage";
import { ProfilePage } from "./pages/ProfilePage";
import { SmsPage } from "./pages/SmsPage";
import { FaceRegisterPage } from "./pages/FaceRegisterPage";
import CrewStatusPage from "./pages/CrewStatusPage";
import SafetyReportPage from "./pages/SafetyReportPage";
import DocumentsPage from "./pages/DocumentsPage";
import LeaveRequestPage from "./pages/LeaveRequestPage";
import CertCreatePage from "./pages/CertCreatePage";
import BoardingSheetPage from "./pages/BoardingSheetPage";
import AlcoholCheckPage from "./pages/AlcoholCheckPage";
import PortalMobileHomePage from "./pages/portal/PortalMobileHomePage";
import PortalProfilePage from "./pages/portal/PortalProfilePage";
import PortalBoardPage from "./pages/portal/PortalBoardPage";
import PortalChatPage from "./pages/portal/PortalChatPage";
import PortalManualsPage from "./pages/portal/PortalManualsPage";
import PortalExpensesPage from "./pages/portal/PortalExpensesPage";
import PortalRequestsPage from "./pages/portal/PortalRequestsPage";
import PortalOfficePage from "./pages/portal/PortalOfficePage";
import { ImpersonationBar } from "./components/ImpersonationBar";
import CallReceiver from "./components/CallReceiver";

interface LoginNotification {
  alerts: { id: string; alert_type: string; severity: string; message: string }[];
  inactiveCrew: { id: string; name: string; daysSince: number | null }[];
  unreadMessages: { senderName: string; count: number }[];
  threshDays: number;
}

const AppInner: React.FC = () => {
  const { user, loading, login, logout, isAuthenticated } = useAuth();
  const nav = useNavigate();
  const [loginNotif, setLoginNotif] = React.useState<LoginNotification | null>(null);
  // 「確認しました」でログイン時通知の確認水位（__login_summary__）を記録する。
  // これが無いと共有端末では打刻のたび（＝再ログインのたび）に同じメッセージ通知が復活する。
  // 連絡タブの未読バッジは本来の既読管理（会話を開いたら既読）のままにする。
  const dismissLoginNotif = React.useCallback(() => {
    setLoginNotif(prev => {
      if (prev && (prev.unreadMessages?.length ?? 0) > 0) {
        apiFetch('/messages/mark-read', { method: 'POST', body: JSON.stringify({ partner_id: '__login_summary__' }) }).catch(() => {});
      }
      return null;
    });
  }, []);
  // 端末登録船舶と配置船舶の不一致でログインをブロックする画面用
  const [boardingBlock, setBoardingBlock] = React.useState<{ deviceVesselName?: string; assignedVesselName?: string } | null>(null);

  // 会社ごとの機能フラグを取得してキャッシュ（新機能の出し分けに使う。取得失敗時は前回値を使う）
  useEffect(() => {
    if (!isAuthenticated) return;
    apiFetch<{ data?: Record<string, boolean> }>('/auth/features')
      .then(r => saveFeatures(r?.data ?? {}))
      .catch(() => { /* オフライン時は前回のキャッシュで判定する */ });
  }, [isAuthenticated]);

  // 通知が許可済みの端末は、購読をサーバーへ取り直す（SW更新や機種変で購読が切れても
  // 自動で復旧し、アプリを閉じていても通知が届き続けるようにする）。許可のポップアップは出さない。
  useEffect(() => {
    if (!isAuthenticated) return;
    if (pushSupported() && Notification.permission === "granted") {
      enablePush().catch(() => { /* 圏外時は次回オンラインで復旧 */ });
    }
  }, [isAuthenticated]);

  // アルコール判定の閾値を取得してキャッシュ（会社ごとに異なるため画面に直接書かない）
  useEffect(() => {
    if (!isAuthenticated) return;
    apiFetch<{ data?: AlcoholThresholds }>('/alert-settings/alcohol')
      .then(r => saveAlcoholThresholds(r?.data))
      .catch(() => { /* オフライン時は前回のキャッシュ、無ければ既定値で表示する */ });
  }, [isAuthenticated]);

  // ログイン後、端末に登録された船舶と船員の配置船舶が一致するか検証（不一致ならブロック）
  useEffect(() => {
    if (!isAuthenticated || !user) { setBoardingBlock(null); return; }
    let cancelled = false;
    verifyBoarding()
      .then(r => { if (!cancelled && !r.match) setBoardingBlock({ deviceVesselName: r.deviceVesselName, assignedVesselName: r.assignedVesselName }); })
      .catch(() => { /* 検証不可時はブロックしない */ });
    return () => { cancelled = true; };
  }, [isAuthenticated, user]);

  // apiFetchを自動同期マネージャーに登録（認証トークンが乗るように）
  useEffect(() => {
    initAutoSync(apiFetch as (path: string, opts?: RequestInit) => Promise<unknown>);
  }, []);

  // 権限情報をフェッチして localStorage に保存（BottomNav / ページ表示制御用）
  useEffect(() => {
    if (!isAuthenticated) {
      localStorage.removeItem("catms_permissions");
      return;
    }
    const load = (initial: boolean) => apiFetch<{ data: Record<string, boolean> }>("/position-permissions/my")
      .then(res => {
        const next = JSON.stringify(res.data);
        const changed = localStorage.getItem("catms_permissions") !== next;
        localStorage.setItem("catms_permissions", next);
        // 陸上で役職が登録・変更されたら、開き直さなくてもメニューに反映する
        if (changed) window.dispatchEvent(new Event("catms-permissions-updated"));
      })
      .catch(() => { if (initial) localStorage.setItem("catms_permissions", JSON.stringify({ _loaded: true })); });
    load(true);
    // アプリを前面に戻したとき＋5分ごとに権限を取り直す（オフライン時は失敗しても前回値を保持）
    const onVisible = () => { if (document.visibilityState === "visible" && navigator.onLine) load(false); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    const timer = setInterval(() => { if (navigator.onLine) load(false); }, 5 * 60_000);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      clearInterval(timer);
    };
  }, [isAuthenticated]);

  // オフライン顔ログインで確立したローカルセッション（トークン空）を、
  // 通信復帰時に本人の顔シークレットで正規トークンへ昇格する。
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const upgrade = async () => {
      if (getAccessToken()) return;               // 既にトークンあり
      await offlineAuthReady();                   // 暗号化ストアの復号完了を待つ
      const secret = getOfflineSecret(user.user_id) ?? getStoredFaceSecret(user.user_id);
      if (!secret) return;                         // シークレット未保存（昇格不可）
      try {
        const data = await faceLogin(user.user_id, secret);
        setTokens(data.access_token);
      } catch { /* まだオフライン等 */ }
    };
    // オンライン時／復帰時に会社全乗員のオフライン情報を一括取得して端末にキャッシュ。
    // これにより共有端末では、オフライン中でも任意の乗員へ切り替えてログインできる。
    // 陸側でパスワード変更しても、次のオンライン時に最新ハッシュが反映される。
    const syncRoster = async () => {
      await upgrade();                     // 先に本人トークンを確保（オフライン顔ログイン復帰時）
      if (!getAccessToken()) return;       // まだオフライン → 取得不可
      // オフライン中に登録した顔特徴量が未送信ならサーバへ再送（登録の完全な有効化）
      try { await syncPendingFaceRegistration(user.user_id); } catch { /* 失敗しても次回再試行 */ }
      try {
        const roster = await fetchOfflineRoster();
        await saveRosterFaces(roster.map(e => ({
          userId: e.user_id, userName: e.user_name,
          descriptor: e.face_descriptor ?? undefined,
          faceSecret: e.secret, userRole: e.user_role, companyId: e.company_id,
        })));
      } catch {
        // 個人スマホは全乗員名簿を取得できない（共有端末専用のため403）。
        // ログイン時に受け取った本人分の顔特徴量だけを顔照合用に保存し、
        // 次回からは顔をかざすだけで入れるようにする。
        try {
          const b = getBundleByUserId(user.user_id);
          // 管理者は、会社の設定（admin_face_login）で許可されているときだけ顔照合用に残す
          const isAdminRole = ["shore_admin", "system_admin"].includes(user.user_role);
          if ((!isAdminRole || hasFeature("admin_face_login")) && b?.face_descriptor && b.face_descriptor.length > 0 && b.secret) {
            await saveRosterFaces([{
              userId: user.user_id, userName: user.user_name,
              descriptor: b.face_descriptor, faceSecret: b.secret,
              userRole: user.user_role, companyId: user.company_id,
            }]);
          }
        } catch { /* ここで失敗してもログインは妨げない */ }
      }
    };
    upgrade();
    syncRoster();
    const onOnline = () => { syncRoster(); };
    window.addEventListener("online", onOnline);
    // 通信が戻ったら、画面の再読み込みより先にトークンを取り直す（utils/netGate。フラグ crew_fast_offline の会社のみ）
    setBeforeOnline(upgrade);
    return () => { window.removeEventListener("online", onOnline); setBeforeOnline(null); };
  }, [isAuthenticated, user]);

  // グローバル自動同期（60秒・online復帰・マウント時）
  useAutoSync();

  if (loading) {
    return (
      <div style={{
        minHeight: "100dvh",
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "linear-gradient(160deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)",
      }}>
        <div style={{
          width: 40, height: 40,
          border: "3px solid rgba(255,255,255,0.2)",
          borderTopColor: "#0ea5e9",
          borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
        }} />
        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
          @keyframes bounce { 0%,80%,100% { transform: translateY(0); } 40% { transform: translateY(-8px); } }
          @keyframes pulse { 0%,100% { transform: scale(1); opacity: 0.6; } 50% { transform: scale(1.18); opacity: 0.1; } }
        `}</style>
      </div>
    );
  }

  if (!isAuthenticated) return (
    <LoginPage onLogin={(id, token, userData) => {
      login(id, token, userData);
      setTimeout(async () => {
        // 初回ログインはフラグのキャッシュが空なので、先に取得してから着地を決める
        // （無いと portal の会社でも初回だけ従来ホームに落ちる）。失敗時は前回キャッシュで判定
        try {
          const r = await apiFetch<{ data?: Record<string, boolean> }>('/auth/features');
          saveFeatures(r?.data ?? {});
        } catch { /* 圏外時は前回値 */ }
        // ポータルの会社の着地:
        //   陸上・システム管理者 → モバイルポータルホーム
        //   個人スマホの船員・パート → スタート画面（何をするか選ぶ。ホームのタイル詰め込み対策）
        // それ以外は従来どおり（かんたん画面を選んでいる端末はそちらへ）。
        const role = (userData as { user_role?: string })?.user_role ?? '';
        const officePortal = hasFeature('portal') && ["shore_admin", "system_admin"].includes(role);
        const personalStart = hasFeature('portal') && !officePortal && isPersonalDevice();
        // 管理者の確認専用モード（crew_admin_monitor）は乗組員の状況に着地する
        nav(officePortal ? '/portal-home' : personalStart ? '/start' : isAdminMonitor() ? '/crew-status' : (isSimpleMode() ? '/simple' : '/home'), { replace: true });
        // ログイン直後通知サマリーを取得
        apiFetch<LoginNotification>('/notifications/login-summary')
          .then(data => {
            if (
              (data.alerts?.length ?? 0) > 0 ||
              (data.inactiveCrew?.length ?? 0) > 0 ||
              (data.unreadMessages?.length ?? 0) > 0
            ) {
              setLoginNotif(data);
            }
          })
          .catch(() => { /* noop */ });
      }, 200);
    }} />
  );

  if (boardingBlock) return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(160deg,#7f1d1d 0%,#991b1b 60%,#b91c1c 100%)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 28, textAlign: "center", fontFamily: "Inter, sans-serif" }}>
      <div style={{ fontSize: 56, marginBottom: 8 }}>⚠️</div>
      <div style={{ color: "#fff", fontSize: 20, fontWeight: 800, marginBottom: 14 }}>この端末では利用できません</div>
      <div style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 14, padding: "18px 20px", maxWidth: 360, color: "#fee2e2", fontSize: 14, lineHeight: 1.8 }}>
        この端末は <strong style={{ color: "#fff" }}>「{boardingBlock.deviceVesselName || "別の船舶"}」</strong> に登録されています。<br />
        あなたの配置は <strong style={{ color: "#fff" }}>「{boardingBlock.assignedVesselName || "別の船舶"}」</strong> です。<br /><br />
        登録船舶と異なるため、ログインできません。<br />陸上管理者に連絡してください（管理者へ通知済みです）。
      </div>
      <button
        onClick={() => { setBoardingBlock(null); logout(); }}
        style={{ marginTop: 26, padding: "13px 40px", background: "#fff", color: "#991b1b", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: "pointer" }}
      >
        ログイン画面に戻る
      </button>
    </div>
  );

  return (
    <>
    {/* ログイン後通知ポップアップ */}
    {loginNotif && (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }} onClick={dismissLoginNotif}>
        {/* 通知が多い日は内容が画面より長くなる。スクロールできるようにし、
            「確認しました」は常に下端に見えるよう sticky にする（小さいスマホで
            ボタンに届かず、閉じ方が分からなくなっていた）。 */}
        <div onClick={e => e.stopPropagation()} style={{
          background: '#fff', borderRadius: 18,
          padding: '24px 22px 0', width: '100%', maxWidth: 360,
          maxHeight: '88dvh', overflowY: 'auto', WebkitOverflowScrolling: 'touch',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}>
          <div style={{ fontWeight: 800, fontSize: 16, color: '#0f172a', marginBottom: 16 }}>
            ログイン時の通知
          </div>

          {/* 未解決アラート */}
          {loginNotif.alerts.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ display: 'inline-flex', width: 18, height: 18, borderRadius: '50%', background: '#fef2f2', alignItems: 'center', justifyContent: 'center', fontSize: 10 }}>⚠</span>
                未解決アラート {loginNotif.alerts.length}件
              </div>
              {loginNotif.alerts.slice(0, 3).map(a => (
                <div key={a.id} style={{
                  background: a.severity === 'critical' ? '#fef2f2' : '#fffbeb',
                  border: `1px solid ${a.severity === 'critical' ? '#fecaca' : '#fde68a'}`,
                  borderRadius: 8, padding: '8px 12px', marginBottom: 6, fontSize: 13, color: '#374151',
                }}>{a.message}</div>
              ))}
              {loginNotif.alerts.length > 3 && (
                <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'right' }}>他 {loginNotif.alerts.length - 3}件</div>
              )}
            </div>
          )}

          {/* 未ログイン乗組員 */}
          {loginNotif.inactiveCrew.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#0369a1', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ display: 'inline-flex', width: 18, height: 18, borderRadius: '50%', background: '#e0f2fe', alignItems: 'center', justifyContent: 'center', fontSize: 10 }}>👤</span>
                {loginNotif.threshDays}日以上未ログインのメンバー
              </div>
              {loginNotif.inactiveCrew.map(c => (
                <div key={c.id} style={{
                  background: '#f0f9ff', border: '1px solid #bae6fd',
                  borderRadius: 8, padding: '7px 12px', marginBottom: 5,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13,
                }}>
                  <span style={{ fontWeight: 600, color: '#1e293b' }}>{c.name}</span>
                  <span style={{ fontSize: 11, color: '#0369a1' }}>
                    {c.daysSince !== null ? `${c.daysSince}日前` : '未ログイン'}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* 未読メッセージ */}
          {(loginNotif.unreadMessages?.length ?? 0) > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#15803d', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ display: 'inline-flex', width: 18, height: 18, borderRadius: '50%', background: '#dcfce7', alignItems: 'center', justifyContent: 'center', fontSize: 10 }}>✉</span>
                未読メッセージ
              </div>
              {loginNotif.unreadMessages.map((m, i) => (
                <div key={i} style={{
                  background: '#f0fdf4', border: '1px solid #bbf7d0',
                  borderRadius: 8, padding: '7px 12px', marginBottom: 5,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13,
                }}>
                  <span style={{ fontWeight: 600, color: '#1e293b' }}>{m.senderName}</span>
                  <span style={{ fontSize: 11, color: '#15803d' }}>{m.count}件</span>
                </div>
              ))}
            </div>
          )}

          <div style={{ position: 'sticky', bottom: 0, background: '#fff', padding: '8px 0 22px', boxShadow: '0 -10px 12px -8px rgba(0,0,0,0.12)' }}>
            <button
              onClick={dismissLoginNotif}
              style={{
                width: '100%', padding: '12px', border: 'none', borderRadius: 10,
                background: '#0f172a', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
              }}
            >
              確認しました
            </button>
          </div>
        </div>
      </div>
    )}
    <ImpersonationBar user={user} login={login} logout={logout} />
    <Routes>
      <Route path="/simple"       element={<SimpleHomePage user={user!} />} />
      <Route path="/home"         element={isAdminMonitor() ? <Navigate to="/crew-status" replace /> : <HomePage user={user!} onLogout={logout} />} />
      <Route path="/timestamp"    element={<TimestampPage user={user!} />} />
      <Route path="/history"      element={<HistoryPage user={user!} />} />
      <Route path="/history/:id"  element={<HistoryDetailPage />} />
      <Route path="/messages"     element={<MessagesPage user={user!} />} />
      <Route path="/alerts"       element={<AlertsPage />} />
      <Route path="/settings"     element={<SettingsPage user={user!} onLogout={logout} />} />
      <Route path="/sync"         element={<SyncPage />} />
      <Route path="/profile"      element={<ProfilePage user={user!} />} />
      <Route path="/sms"          element={<SmsPage user={user!} />} />
      <Route path="/face-register" element={<FaceRegisterPage user={user!} />} />
      <Route path="/crew-status"   element={<CrewStatusPage user={user!} />} />
      <Route path="/safety-report" element={<SafetyReportPage user={user!} />} />
      <Route path="/documents" element={<DocumentsPage />} />
      <Route path="/leave"          element={<LeaveRequestPage user={user!} />} />
      <Route path="/cert-create"    element={<CertCreatePage user={user!} />} />
      {/* 乗下船表（船員向け・機能フラグ crew_boarding_sheet の会社のみ入口を表示） */}
      <Route path="/boarding-sheet" element={<BoardingSheetPage user={user!} />} />
      <Route path="/alcohol-check"  element={<AlcoholCheckPage user={user!} />} />
      {/* カツオポータル（機能フラグ portal の会社のみ入口を表示） */}
      <Route path="/start"           element={<PersonalStartPage />} />
      <Route path="/portal-home"     element={<PortalMobileHomePage />} />
      <Route path="/portal-profile"  element={<PortalProfilePage />} />
      <Route path="/portal-board"    element={<PortalBoardPage />} />
      <Route path="/portal-chat"     element={<PortalChatPage user={user!} />} />
      <Route path="/portal-manuals"  element={<PortalManualsPage />} />
      <Route path="/portal-expenses" element={<PortalExpensesPage />} />
      <Route path="/portal-requests" element={<PortalRequestsPage />} />
      <Route path="/portal-office"   element={<PortalOfficePage />} />
      <Route path="*"             element={<Navigate to={isAdminMonitor() ? "/crew-status" : isSimpleMode() ? "/simple" : "/home"} replace />} />
    </Routes>
    </>
  );
};

export const App: React.FC = () => (
  <BrowserRouter>
    <AppInner />
    <CallReceiver />
  </BrowserRouter>
);

